import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import ProductCard from "../components/ProductCard";
import { ChevronRight, Wine, Sprout, Package, Award } from "lucide-react";

const HERO_IMG =
    "https://static.prod-images.emergentagent.com/jobs/29081a3e-ac58-482c-aff5-9e6ace12b60e/images/719d0b0d6ba3622fdddca2ef2d647ac75dec06bc67800af2abb7dec03ecd00f3.png";
const PRODUCER_IMG =
    "https://static.prod-images.emergentagent.com/jobs/29081a3e-ac58-482c-aff5-9e6ace12b60e/images/78f8b1c5a7dc4063e49935198bcf10f414cd58f3b196edea1ad97750c90d96ed.png";
const OIL_IMG =
    "https://static.prod-images.emergentagent.com/jobs/29081a3e-ac58-482c-aff5-9e6ace12b60e/images/b30e383a659f5ab2fd5d39196fa9a13b882ff9621acadcdd84293883937313f7.png";
const CHEESE_IMG = "https://images.pexels.com/photos/13996604/pexels-photo-13996604.jpeg";
const WINE_IMG = "https://images.pexels.com/photos/5004027/pexels-photo-5004027.jpeg";

// Imagem distinta e coerente por produtor (evita que todos partilhem o mesmo cover_image)
function imageForProducer(p, idx = 0) {
    const region = (p.region || "").toLowerCase();
    if (region.includes("alentejo")) return CHEESE_IMG;
    if (region.includes("beira")) return OIL_IMG;
    if (region.includes("dão") || region.includes("dao")) return PRODUCER_IMG;
    if (region.includes("douro")) return HERO_IMG;
    if (region.includes("vinho verde")) return WINE_IMG;
    const pool = [HERO_IMG, CHEESE_IMG, PRODUCER_IMG, OIL_IMG, WINE_IMG];
    return pool[idx % pool.length];
}

export default function Home() {
    const [wines, setWines] = useState([]);
    const [products, setProducts] = useState([]);
    const [producers, setProducers] = useState([]);
    const [wineFilter, setWineFilter] = useState("todos");

    useEffect(() => {
        api.get("/products?kind=wine&limit=24").then((r) => setWines(r.data)).catch(() => {});
        api.get("/products?kind=product&limit=8").then((r) => setProducts(r.data)).catch(() => {});
        api.get("/producers").then((r) => setProducers(r.data.slice(0, 4))).catch(() => {});
    }, []);

    const wineType = (p) => {
        const c = (p.category || "").toLowerCase();
        const t = (p.wine?.wine_type || "").toLowerCase();
        if (c.includes("porto") || t === "porto") return "porto";
        if (c.includes("espumante") || t === "espumante") return "espumante";
        if (c.includes("branco") || t === "branco") return "branco";
        if (c.includes("tinto") || t === "tinto") return "tinto";
        return "outro";
    };
    const WINE_FILTERS = [
        { key: "todos", label: "Todos" },
        { key: "tinto", label: "Tinto" },
        { key: "branco", label: "Branco" },
        { key: "porto", label: "Porto" },
        { key: "espumante", label: "Espumante" },
    ];
    const filteredWines = (wineFilter === "todos" ? wines : wines.filter((p) => wineType(p) === wineFilter)).slice(0, 8);

    return (
        <>
            <Navbar variant="dark" />

            {/* HERO */}
            <section className="relative h-[100vh] min-h-[680px] overflow-hidden bg-wine-deep hero-grain">
                <div className="absolute inset-0">
                    <img
                        src={HERO_IMG}
                        alt="Vinhos artesanais portugueses"
                        className="w-full h-full object-cover kenburns"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-wine-deep/30 via-wine-deep/40 to-wine-deep/90" />
                </div>
                <div className="relative h-full container-edge flex flex-col justify-end pb-24 md:pb-32">
                    <div className="max-w-3xl animate-fade-up">
                        <span className="label-overline text-gold-soft" data-testid="hero-overline">
                            Marketplace premium · Portugal artesanal
                        </span>
                        <h1 className="mt-6 font-heading font-light text-cream text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight text-shadow-soft">
                            Vinhos e produtos artesanais de Portugal,{" "}
                            <em className="text-gold-soft not-italic font-normal">direto de quem faz</em>.
                        </h1>
                        <p className="mt-8 max-w-xl text-cream/85 text-base md:text-lg leading-relaxed">
                            Uma curadoria editorial de pequenos produtores portugueses. Vinhos de
                            socalcos, azeites de oliveiras centenárias, queijos curados em cave,
                            doçaria conventual. Cada caixa, uma história.
                        </p>
                        <div className="mt-12 flex flex-wrap items-center gap-4">
                            <Link to="/marketplace" className="btn-gold" data-testid="cta-marketplace">
                                Explorar Marketplace
                            </Link>
                            <Link to="/clube" className="btn-dark-outline" data-testid="cta-clube">
                                Conhecer o Clube
                            </Link>
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-6 left-0 right-0 container-edge flex items-center justify-between text-cream/50 text-xs label-overline">
                    <span>Estabelecido em Portugal</span>
                    <span className="hidden md:block">Curadoria sazonal · Edições limitadas</span>
                </div>
            </section>

            {/* TRUST / VALUES STRIP */}
            <section className="bg-cream">
                <div className="container-edge py-12 grid grid-cols-2 md:grid-cols-4 gap-12 text-center">
                    {[
                        { icon: Wine, label: "Pequenos produtores", desc: "Famílias vinhateiras e artesãos" },
                        { icon: Sprout, label: "Origem certificada", desc: "Castas e regiões portuguesas" },
                        { icon: Package, label: "Embalagem cuidada", desc: "Embalado à mão, com afeto" },
                        { icon: Award, label: "Curadoria editorial", desc: "Selecionado por sommeliers" },
                    ].map(({ icon: Icon, label, desc }) => (
                        <div key={label} className="flex flex-col items-center gap-3">
                            <Icon className="h-7 w-7 text-wine" strokeWidth={1.2} />
                            <span className="label-overline text-wine-dark">{label}</span>
                            <span className="text-xs text-wine-dark/60 max-w-[140px]">{desc}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* VINHOS */}
            <section className="bg-cream pt-20 md:pt-28 pb-16 md:pb-20" data-testid="home-wines">
                <div className="container-edge">
                    <div className="flex items-end justify-between mb-10 md:mb-12">
                        <div>
                            <span className="label-overline text-wine flex items-center gap-2">
                                <Wine className="h-4 w-4" strokeWidth={1.5} /> Garrafeira
                            </span>
                            <h2 className="mt-4 font-heading text-4xl md:text-5xl text-wine-dark max-w-2xl leading-[1.05]">
                                Vinhos artesanais portugueses
                            </h2>
                            <p className="mt-3 text-wine-dark/60 text-sm md:text-base max-w-xl">
                                De socalcos do Douro a herdades alentejanas — selecionados garrafa a garrafa.
                            </p>
                        </div>
                        <Link to="/marketplace" className="hidden md:inline-flex items-center gap-2 label-overline text-wine hover:text-gold-soft transition-colors shrink-0">
                            Ver todos os vinhos <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                        </Link>
                    </div>
                    {/* Filtro por tipo */}
                    <div className="flex flex-wrap gap-2 mb-8" data-testid="wine-type-filter">
                        {WINE_FILTERS.map((f) => (
                            <button
                                key={f.key}
                                onClick={() => setWineFilter(f.key)}
                                className={`label-overline text-[10px] px-4 py-2 rounded-full border transition-all duration-300
                                    ${wineFilter === f.key
                                        ? "bg-wine text-cream border-wine shadow-sm"
                                        : "bg-transparent text-wine-dark border-wine-dark/15 hover:border-wine/50 hover:text-wine"}`}
                                data-testid={`wine-filter-${f.key}`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                        {filteredWines.map((p) => (
                            <ProductCard key={p.id} product={p} />
                        ))}
                        {filteredWines.length === 0 && (
                            <p className="col-span-full text-center py-12 text-wine-dark/50 label-overline" data-testid="wine-empty">
                                Sem vinhos deste tipo de momento.
                            </p>
                        )}
                    </div>
                    <div className="mt-10 flex md:hidden justify-center">
                        <Link to="/marketplace" className="btn-wine-outline text-xs">Ver todos os vinhos</Link>
                    </div>
                </div>
            </section>

            {/* PRODUTOS / MERCEARIA */}
            <section className="bg-cream-warm py-16 md:py-24 border-y border-gold-soft/25" data-testid="home-products">
                <div className="container-edge">
                    <div className="flex items-end justify-between mb-10 md:mb-12">
                        <div>
                            <span className="label-overline text-wine flex items-center gap-2">
                                <Sprout className="h-4 w-4" strokeWidth={1.5} /> Mercearia fina
                            </span>
                            <h2 className="mt-4 font-heading text-4xl md:text-5xl text-wine-dark max-w-2xl leading-[1.05]">
                                Sabores da despensa portuguesa
                            </h2>
                            <p className="mt-3 text-wine-dark/60 text-sm md:text-base max-w-xl">
                                Azeites, queijos, enchidos e doçaria conventual de pequenos produtores.
                            </p>
                        </div>
                        <Link to="/marketplace" className="hidden md:inline-flex items-center gap-2 label-overline text-wine hover:text-gold-soft transition-colors shrink-0">
                            Ver todos os produtos <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                        {products.slice(0, 8).map((p) => (
                            <ProductCard key={p.id} product={p} />
                        ))}
                    </div>
                    <div className="mt-10 flex md:hidden justify-center">
                        <Link to="/marketplace" className="btn-wine-outline text-xs">Ver todos os produtos</Link>
                    </div>
                </div>
            </section>

            {/* PRODUCER STORY — asymmetric editorial */}
            <section className="bg-cream-warm py-24 md:py-32 relative overflow-hidden border-y border-gold-soft/30">
                <div className="relative container-edge grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7 order-2 lg:order-1">
                        <img
                            src={PRODUCER_IMG}
                            alt="Produtor português"
                            className="w-full h-[520px] md:h-[640px] object-cover rounded-sm shadow-xl"
                        />
                    </div>
                    <div className="lg:col-span-5 order-1 lg:order-2">
                        <span className="label-overline text-gold-deep">Produtores</span>
                        <h2 className="mt-6 font-heading text-4xl md:text-5xl leading-[1.05] text-wine-dark">
                            Onde cada garrafa carrega o nome de quem a fez.
                        </h2>
                        <div className="divider-curl mt-8 mb-8 text-wine" />
                        <p className="text-wine-dark/75 leading-relaxed">
                            Da Quinta do Vale Escuro no Douro Superior, à Herdade do Sobral no
                            coração alentejano. Conhecemos cada produtor pessoalmente. Visitamos as
                            adegas. Provamos com eles. Só então os trazemos até si.
                        </p>
                        <Link to="/produtores" className="mt-10 inline-flex items-center gap-2 label-overline text-wine hover:text-gold-deep transition-colors" data-testid="producers-cta">
                            Conhecer os produtores <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* CLUBE */}
            <section className="bg-beige-natural py-24 md:py-32">
                <div className="container-edge">
                    <div className="flex flex-col items-center text-center mb-20">
                        <span className="label-overline text-wine">Clube Caseirinho</span>
                        <h2 className="mt-6 font-heading text-4xl md:text-6xl text-wine-dark max-w-3xl leading-[1.05]">
                            Uma curadoria de vinhos artesanais que chega todos os meses.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                        {[
                            { name: "Pack 4 Vinhos", price: "€39,90", desc: "4 vinhos artesanais portugueses todos os meses.", tag: "Iniciar" },
                            { name: "Pack 6 Vinhos", price: "€59,90", desc: "6 vinhos selecionados de pequenos produtores.", tag: "Mais popular" },
                            { name: "Reserva Premium", price: "€99,90", desc: "Edições limitadas e reservas raras.", tag: "Exclusivo" },
                        ].map((plan, idx) => (
                            <div key={plan.name} className={`bg-white p-10 rounded-md border ${idx === 1 ? "border-gold-soft shadow-xl" : "border-gold-soft/20 shadow-sm"} flex flex-col gap-6`}>
                                <span className="label-overline text-gold-soft">{plan.tag}</span>
                                <h3 className="font-heading text-3xl text-wine-dark">{plan.name}</h3>
                                <p className="text-sm text-wine-dark/70 leading-relaxed">{plan.desc}</p>
                                <div className="flex items-baseline gap-1">
                                    <span className="font-heading text-5xl text-wine-dark">{plan.price}</span>
                                    <span className="text-sm text-wine-dark/60">/mês</span>
                                </div>
                                <Link to="/clube" className="btn-wine mt-auto" data-testid={`clube-${idx}-cta`}>
                                    Saber mais
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CATEGORIES grid editorial */}
            <section className="bg-cream-warm py-24 md:py-32">
                <div className="container-edge">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
                        <Link to="/marketplace?category=Azeite" className="lg:col-span-8 group relative h-[420px] overflow-hidden rounded-md">
                            <img src={OIL_IMG} alt="Azeite" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                            <div className="absolute inset-0 bg-gradient-to-t from-wine-dark/85 to-transparent" />
                            <div className="absolute bottom-8 left-8 text-cream">
                                <span className="label-overline text-gold-soft">Mercearia fina</span>
                                <h3 className="font-heading text-4xl md:text-5xl mt-3">Azeites & Mercearia</h3>
                            </div>
                        </Link>
                        <Link to="/marketplace?category=Queijos" className="lg:col-span-4 group relative h-[420px] overflow-hidden rounded-md">
                            <img src={CHEESE_IMG} alt="Queijos" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                            <div className="absolute inset-0 bg-gradient-to-t from-wine-dark/85 to-transparent" />
                            <div className="absolute bottom-8 left-8 text-cream">
                                <span className="label-overline text-gold-soft">Charcutaria</span>
                                <h3 className="font-heading text-4xl md:text-5xl mt-3">Queijos & Enchidos</h3>
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            {/* PRODUCERS strip */}
            <section className="bg-beige-natural py-24">
                <div className="container-edge">
                    <div className="flex items-end justify-between mb-12">
                        <h2 className="font-heading text-4xl md:text-5xl text-wine-dark">Nas adegas portuguesas</h2>
                        <Link to="/produtores" className="label-overline text-wine hover:text-gold-soft transition-colors">Ver todos</Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {producers.map((p, idx) => (
                            <Link to={`/produtores/${p.id}`} key={p.id} className="group block" data-testid={`producer-${p.id}`}>
                                <div className="aspect-[3/4] overflow-hidden bg-beige-warm">
                                    <img src={imageForProducer(p, idx)} alt={p.producer_name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                </div>
                                <div className="mt-4">
                                    <span className="label-overline text-wine/60">{p.region}</span>
                                    <h3 className="font-heading text-xl text-wine-dark mt-1">{p.producer_name}</h3>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <Footer />
        </>
    );
}
